/**
 * Renders Payload Lexical rich text content to React elements.
 * Recursive renderer for the Lexical JSON AST format.
 */
export default function RichText({ content }) {
  if (!content || !content.root || !content.root.children) return null;

  return (
    <div className="rich-text font-inter text-gray-cool-700 text-[17px] leading-[1.9]">
      {content.root.children.map((node, i) => (
        <RichTextNode key={i} node={node} />
      ))}
    </div>
  );
}

function RichTextNode({ node }) {
  switch (node.type) {
    case "paragraph":
      return (
        <p className="mb-6">
          {node.children?.map((child, i) => (
            <RichTextNode key={i} node={child} />
          ))}
        </p>
      );

    case "heading": {
      const styles = {
        h1: "text-[36px] font-extrabold tracking-[-1px] text-gray-cool-900 mt-12 mb-6",
        h2: "text-[28px] font-bold tracking-[-0.5px] text-gray-cool-900 mt-10 mb-5",
        h3: "text-[22px] font-bold text-gray-cool-900 mt-8 mb-4",
        h4: "text-[18px] font-semibold text-gray-cool-900 mt-6 mb-3",
        h5: "text-[16px] font-semibold text-gray-cool-800 mt-5 mb-2",
        h6: "text-[14px] font-semibold text-gray-cool-800 mt-4 mb-2",
      };
      const tag = node.tag || "h2";
      const Tag = tag;
      return (
        <Tag className={styles[tag] || styles.h2}>
          {node.children?.map((child, i) => (
            <RichTextNode key={i} node={child} />
          ))}
        </Tag>
      );
    }

    case "text": {
      let el = node.text;
      if (!el && el !== 0) return null;
      // Lexical format is a bitmask: 1=bold, 2=italic, 4=strikethrough, 8=underline, 16=code
      if (node.format & 1) el = <strong className="font-bold text-gray-cool-900">{el}</strong>;
      if (node.format & 2) el = <em>{el}</em>;
      if (node.format & 4) el = <s>{el}</s>;
      if (node.format & 8) el = <u>{el}</u>;
      if (node.format & 16)
        el = (
          <code className="bg-gray-cool-100 text-blue-primary-600 px-1.5 py-0.5 rounded text-[15px] font-mono">
            {el}
          </code>
        );
      return el;
    }

    case "link":
    case "autolink":
      return (
        <a
          href={node.fields?.url || node.url}
          target={node.fields?.newTab ? "_blank" : undefined}
          rel={node.fields?.newTab ? "noopener noreferrer" : undefined}
          className="text-blue-primary-500 hover:text-blue-primary-700 underline underline-offset-2 transition-colors"
        >
          {node.children?.map((child, i) => (
            <RichTextNode key={i} node={child} />
          ))}
        </a>
      );

    case "list": {
      const ListTag = node.listType === "number" ? "ol" : "ul";
      const listStyle =
        node.listType === "number"
          ? "list-decimal pl-6 mb-6 space-y-2"
          : "list-disc pl-6 mb-6 space-y-2";
      return (
        <ListTag className={listStyle}>
          {node.children?.map((child, i) => (
            <RichTextNode key={i} node={child} />
          ))}
        </ListTag>
      );
    }

    case "listitem":
      return (
        <li className="leading-[1.8]">
          {node.children?.map((child, i) => (
            <RichTextNode key={i} node={child} />
          ))}
        </li>
      );

    case "quote":
      return (
        <blockquote className="border-l-4 border-blue-primary-300 pl-6 py-2 my-8 text-gray-cool-600 italic text-[18px]">
          {node.children?.map((child, i) => (
            <RichTextNode key={i} node={child} />
          ))}
        </blockquote>
      );

    case "upload":
      if (node.value?.url) {
        return (
          <figure className="my-8">
            <img
              src={node.value.url}
              alt={node.value.alt || ""}
              className="rounded-xl w-full"
              loading="lazy"
            />
            {node.value.caption && (
              <figcaption className="text-[14px] text-gray-cool-500 mt-3 text-center">
                {node.value.caption}
              </figcaption>
            )}
          </figure>
        );
      }
      return null;

    case "horizontalrule":
      return <hr className="my-10 border-gray-cool-200" />;

    case "linebreak":
      return <br />;

    default:
      if (node.children) {
        return node.children.map((child, i) => (
          <RichTextNode key={i} node={child} />
        ));
      }
      return null;
  }
}
